// Section 21 and 24.10: ledgerOf(state) derives the full Ledger (eleven sections, the tags, the
// character sketch and the score) from the accumulator in state (written by the decay phase's
// ledger sub-step, phases/decay/ledger.ts) plus people, formerPeople, cases, history and series.
// steadyOf(state) reports the Section 20 conditions as they stand now. continueInSandbox(state)
// is the Section 20 "continue playing in sandbox" after a win. All pure and deterministic.
import { produce } from 'immer';
import { config, type Config } from '../config';
import { SimError } from '../core/errors';
import { mean } from '../core/math';
import { peopleSorted } from '../core/queries';
import { sortedValues } from '../core/sorted';
import type {
  AgeGroup,
  DeathCause,
  GameState,
  JusticeResponse,
  Ledger,
  LedgerShortRow,
  PersonId,
  SteadyResult,
} from '../core/types';
import { steadyConditions } from '../phases/steady/conditions';
import { readContext } from './context';
import {
  caseRows,
  DEPRIVATION_CAUSES,
  earlyDeaths,
  fateOf,
  fragilityMarks,
  groupFate,
  lifeExpectancy,
  meanAgeAtDeath,
  peakOf,
  settledNewcomers,
  steadyResults,
} from './ledger/facts';
import { characterSketch } from './ledger/sketch';
import { arrivalShares, ledgerTags } from './ledger/tags';

export type { SketchTemplateId } from './ledger/sketch';
export { SKETCH_MAX_LINES, SKETCH_MIN_LINES } from './ledger/sketch';

/**
 * The steady-state target the counter runs toward: the new-game target, or twice it once the
 * player continues in sandbox after a win (Section 20 "a longer target", G-08).
 */
export function steadyTarget(state: GameState): number {
  const base = state.options.steadyTargetYears;
  return state.sandbox ? base * 2 : base;
}

/** Section 20: after a win, keep playing; the counter keeps running toward a longer target. */
export function continueInSandbox(state: GameState): GameState {
  if (state.outcome?.kind !== 'won')
    throw new SimError('bad_status', 'sandbox is offered only after a win', { turn: state.turn });
  if (state.sandbox) return state;
  return produce(state, (d) => {
    d.sandbox = true;
  });
}

function names(state: GameState): Record<PersonId, string> {
  const out: Record<PersonId, string> = {};
  for (const f of sortedValues(state.formerPeople)) out[f.id] = f.name;
  for (const p of peopleSorted(state)) out[p.id] = p.name;
  return out;
}

export function ledgerOf(state: GameState, cfg: Config = config): Ledger {
  const acc = state.ledger;
  const people = peopleSorted(state);

  const deathsByCause: Partial<Record<DeathCause, number>> = {};
  for (const d of acc.deaths) deathsByCause[d.cause] = (deathsByCause[d.cause] ?? 0) + 1;
  const deprivationDeaths = acc.deaths.filter((d) => DEPRIVATION_CAUSES.has(d.cause)).length;
  let oldest: (typeof people)[number] | null = null;
  for (const p of people) if (oldest === null || p.ageSeasons > oldest.ageSeasons) oldest = p;
  let youngest: (typeof acc.deaths)[number] | null = null;
  for (const d of acc.deaths)
    if (youngest === null || d.ageSeasons < youngest.ageSeasons) youngest = d;

  const shortShareByGroup = {} as Record<AgeGroup, number>;
  for (const g of ['child', 'adult', 'elder'] as const) {
    const ps = acc.personSeasons[g];
    shortShareByGroup[g] = ps.total === 0 ? 0 : ps.short / ps.total;
  }
  const records: LedgerShortRow[] = sortedValues(acc.wentWithout)
    .map((r) => ({ ...r, fate: fateOf(state, r.personId).fate }))
    .sort((a, b) => b.seasonsShort - a.seasonsShort || (a.personId < b.personId ? -1 : 1));

  const cases = caseRows(state);
  const responses: Partial<Record<JusticeResponse, number>> = {};
  for (const c of cases)
    if (c.response !== null) responses[c.response] = (responses[c.response] ?? 0) + 1;

  const gate = arrivalShares(state);
  const gini = peakOf(state.series.gini);
  const fragility = peakOf(state.series.fragility);
  const finalLiteracy = state.series.literacy[state.series.literacy.length - 1] ?? 0;
  const le = lifeExpectancy(state);
  const tags = ledgerTags(state, cfg, {
    earlyDeaths: earlyDeaths(state),
    settledNewcomers: settledNewcomers(state, cfg.ledger.foundersClubMinSeasons),
    finalLiteracy,
  });
  const steady = steadyResults(state);
  let bestStreak = 0;
  for (const r of steady) bestStreak = Math.max(bestStreak, r.counter);
  const target = steadyTarget(state);

  return {
    outcome: {
      outcome: state.outcome,
      seasons: state.turn,
      seed: state.seed,
      options: state.options,
      ironman: state.options.ironman,
      sandbox: state.sandbox,
      steadyCounter: state.steady.counter,
      steadyTarget: target,
    },
    people: {
      births: acc.births,
      deathsByCause,
      peakPopulation: Math.max(acc.peakPopulation.value, people.length),
      finalPopulation: people.length,
      lifeExpectancySeasons: le,
      oldest:
        oldest === null
          ? null
          : { personId: oldest.id, name: oldest.name, ageSeasons: oldest.ageSeasons },
      youngestDeath: youngest,
      meanAgeAtDeathSeasons: meanAgeAtDeath(state),
      deprivationDeaths,
    },
    wentWithout: { records, shortShareByGroup, personSeasons: acc.personSeasons },
    chosen: acc.shortageDecisions,
    norms: acc.normEvents,
    justice: {
      cases: cases.length,
      corporalPunishments: acc.corporalPunishments,
      executions: acc.executions,
      exiles: acc.exiles,
      caseRows: cases,
      responses,
    },
    gate: {
      arrivals: acc.arrivals,
      admittedShare: gate.arrivals === 0 ? 0 : gate.admitted / gate.arrivals,
      turnedAwayShare: gate.arrivals === 0 ? 0 : gate.turnedAway / gate.arrivals,
      departures: acc.departures,
      founders: groupFate(state, 'founder'),
      born: groupFate(state, 'born'),
      newcomers: groupFate(state, 'newcomer'),
    },
    inequality: {
      series: state.series.gini,
      peak: gini.peak,
      peakTurn: gini.turn,
      mean: mean(state.series.gini),
    },
    knowledge: {
      literacySeries: state.series.literacy,
      skillsLost: acc.skillsLost,
      improvements: acc.improvements,
      finalLiteracy,
    },
    fragility: {
      series: state.series.fragility,
      marks: fragilityMarks(state),
      peak: fragility.peak,
      peakTurn: fragility.turn,
      mean: mean(state.series.fragility),
    },
    policyChanges: acc.policyChanges,
    names: names(state),
    sketch: characterSketch(state, cfg, {
      deprivationDeaths,
      shortShareByGroup,
      finalLiteracy,
      foundersClub: tags.includes('founders-club'),
      steadyTarget: target,
    }),
    tags,
    score: {
      seasonsSurvived: state.turn,
      steadyYears: steady.filter((r) => r.allPass).length,
      bestStreak,
      rationingDeaths: deprivationDeaths,
      corporalPunishments: acc.corporalPunishments,
      executions: acc.executions,
      turnedAwayStarvingFamilies: acc.arrivals.filter(
        (a) => a.decision === 'turn-away' && a.starvingFamily,
      ).length,
    },
  };
}

/** The Section 20 conditions as they stand now (the counter is the last Winter's). */
export function steadyOf(state: GameState, cfg: Config = config): SteadyResult {
  const ctx = readContext(state, cfg);
  const conditions = steadyConditions(ctx);
  return {
    turn: state.turn,
    year: state.year,
    conditions,
    allPass: Object.values(conditions).every((c) => c.pass),
    counter: state.steady.counter,
    target: steadyTarget(state),
  };
}
